import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CityService from '../../../services/city.service';
import SalePointTypeService from '../../../services/salePointType.service';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px',
      minWidth: '500px'
    }
  },
  dialogTitle: {
    display: 'flex', 
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column', 
    gap: '20px',
    padding: '24px'
  },
  actions: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  },
  submitButton: {
    backgroundColor: 'primary.main',
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#333'
    }
  },
  cancelButton: {
    color: '#666',
    borderRadius: '8px',
    textTransform: 'none'
  }
};

function Form({ open, onClose, initialData = null, onSubmit, onCancel }) {
  const [formData, setFormData] = React.useState({
    name: '',
    address: '',
    opening_period: '',
    city_id: '',
    sales_point_type_id: ''
  });

  const [cities, setCities] = React.useState([]);
  const [salePointTypes, setSalePointTypes] = React.useState([]);
  const [errors, setErrors] = React.useState({});
  const [generalError, setGeneralError] = React.useState(null);

  React.useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [citiesData, typesData] = await Promise.all([
          CityService.list(),
          SalePointTypeService.list()
        ]);
        setCities(citiesData);
        setSalePointTypes(typesData);
      } catch (error) {
        console.error('Erreur lors du chargement des données de référence:', error);
      }
    };
    loadReferenceData();
  }, []);

  React.useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        address: initialData.address || '',
        opening_period: initialData.opening_period || '',
        city_id: initialData.city.id || '',
        sales_point_type_id: initialData.sale_point_type.id || ''
      });
    } else {
      setFormData({
        name: '',
        address: '',
        opening_period: '',
        city_id: '',
        sales_point_type_id: ''
      });
    }
    setErrors({});
    setGeneralError(null);
  }, [initialData, open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors({});
    setGeneralError(null);

    const errorCallback = (validationErrors) => {
      const errorsObj = {};
      let generalMessage = null;
      if (validationErrors && validationErrors.length > 0) {
        validationErrors.forEach(err => {
          if (err.field) {
            errorsObj[err.field] = err.message;
          } else {
            generalMessage = err.message || 'Une erreur est survenue';
          }
        });
      }
      setErrors(errorsObj);
      setGeneralError(generalMessage);
    };

    const submittedData = {
      ...formData,
      id: initialData?.id
    };
    try {
      if (onSubmit) {
        await onSubmit(submittedData, errorCallback);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const handleCancel = () => {
    onCancel();
    setFormData({
      name: '',
      address: '',
      opening_period: '',
      city_id: '',
      sales_point_type_id: ''
    });
    setErrors({});
    setGeneralError(null);
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <span>{initialData ? 'Modifier le point de vente' : 'Nouveau point de vente'}</span>
        <IconButton
          onClick={handleCancel}
          size="small"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={styles.form}>
          {generalError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
              {generalError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            size="small"
            error={!!errors.name}
            helperText={errors.name}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="Adresse"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
            multiline
            rows={2}
            size="small"
            error={!!errors.address}
            helperText={errors.address}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="Période d'ouverture"
            name="opening_period"
            value={formData.opening_period}
            onChange={handleChange}
            required
            size="small"
            error={!!errors.opening_period}
            helperText={errors.opening_period}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <FormControl fullWidth size="small" error={!!errors.city_id}>
            <InputLabel id="city-label">Ville</InputLabel>
            <Select
              labelId="city-label"
              label="Ville"
              name="city_id"
              value={formData.city_id}
              onChange={handleChange}
              required
            >
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
            {errors.city_id && <FormHelperText>{errors.city_id}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth size="small" error={!!errors.sales_point_type_id}>
            <InputLabel id="type-label">Type de point de vente</InputLabel>
            <Select
              labelId="type-label"
              label="Type de point de vente"
              name="sales_point_type_id"
              value={formData.sales_point_type_id}
              onChange={handleChange}
              required
            >
              {salePointTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
            {errors.sales_point_type_id && <FormHelperText>{errors.sales_point_type_id}</FormHelperText>}
          </FormControl>
        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button
            onClick={handleCancel}
            sx={styles.cancelButton}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={styles.submitButton}
          >
            {initialData ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    opening_period: PropTypes.string,
    city_id: PropTypes.number,
    sales_point_type_id: PropTypes.number
  })
};

export default Form;